import React, { useContext, useEffect, useState } from "react";
import QRCode from "qrcode.react";
import { Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import openSocket from "../../services/socket-io";
import {
	connectSocket,
	addGoWebSocketMessageHandler,
	addGoWebSocketOpenHandler,
} from "../../services/socketv2";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import AppModal from "../AppModal";

const useStyles = makeStyles(() => ({
	modalContainer: {
		padding: "0px",
	},
	modalQR: {
		background: "#ffffff",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-evenly",
		height: "350px",
		width: "750px",
		padding: "0px",
	},
	textContainer: {
		width: "350px",
	},
	titleText: {
		color: "#009688",
	},
	instructionText: {
		color: "black",
	},
	listItem: {
		marginBottom: "10px",
	},
}));

function QrcodeModal({ open, onClose, whatsAppId }) {
	const classes = useStyles();
	const [qrCode, setQrCode] = useState("");
	const { user } = useContext(AuthContext);

	useEffect(() => {
		if (!open || !whatsAppId || !user?.id) return undefined;

		const socket = openSocket({
			scope: "qrcode",
			userId: user.id,
			companyId: user.companyId,
			component: "QrcodeModal",
		});
		if (!socket) return undefined;

		socket.on("whatsappSession", (data) => {
			if (data.action === "update" && data.session.id === whatsAppId) {
				setQrCode(data.session.qrcode);
			}

			if (data.action === "update" && data.session.qrcode === "") {
				onClose();
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [open, whatsAppId, user?.id, user?.companyId, onClose]);

	useEffect(() => {
		if (!open || !whatsAppId || !user?.id) return undefined;

		const handleMessage = (event) => {
			let data;
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}

			if (data.event !== "qrcode") return;

			if (data.action === "update" && data.session?.id === whatsAppId) {
				setQrCode(data.session.qrcode);

				if (!data.session.qrcode) {
					onClose();
				}
			}
		};

		const fetchQr = () => {
			apiV2
				.get(`/qrcode`, {
					params: {
						connectionId: whatsAppId,
						userId: user.id,
					},
				})
				.catch(() => {});
		};

		const removeMessage = addGoWebSocketMessageHandler(handleMessage);
		const removeOpen = addGoWebSocketOpenHandler(fetchQr);
		connectSocket({
			userId: user.id,
			companyId: user.companyId ?? 0,
		});

		return () => {
			removeMessage();
			removeOpen();
		};
	}, [open, whatsAppId, user?.id, user?.companyId, onClose]);

	return (
		<AppModal
			open={open}
			onClose={onClose}
			maxWidth={750}
			hideHeader
			bodyClassName="!overflow-visible !p-0"
		>
			<Paper elevation={0} className={classes.modalQR}>
				<Typography
					color="primary"
					gutterBottom
					className={classes.textContainer}
				>
					<h2 className={classes.titleText}>{i18n.t("qrCode.title")}</h2>
					<ol className={classes.instructionText}>
						<li className={classes.listItem}>
							{i18n.t("qrCode.firstPoint")}
						</li>
						<li className={classes.listItem}>
							{i18n.t("qrCode.secondPoint.partOne")}
							<b>{i18n.t("qrCode.secondPoint.moreOptions")}</b>
							<img
								src="https://img.icons8.com/material-rounded/24/737373/menu-2.png"
								alt="menu-burguer-options"
							/>
							{i18n.t("qrCode.secondPoint.partTwo")}
							<b>{i18n.t("qrCode.secondPoint.configurations")}</b>
							<img
								src="https://img.icons8.com/material-outlined/24/737373/gear--v1.png"
								alt="icont-settings"
							/>{" "}
							{i18n.t("qrCode.secondPoint.partThree")}
						</li>
						<li className={classes.listItem}>
							{i18n.t("qrCode.thirdPoint")}
						</li>
						<li className={classes.listItem}>
							{i18n.t("qrCode.fourthPoint")}
						</li>
					</ol>
				</Typography>
				{qrCode ? (
					<QRCode value={qrCode} size={256} />
				) : (
					<span>Waiting for QR Code</span>
				)}
			</Paper>
		</AppModal>
	);
}

export default React.memo(QrcodeModal);
